import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { notification } from "antd";
import TicketingService from "services/TicketingService";

export const initialState = {
  data: [],
  dataDetail: [],
  dataTemplate: null,
  loading: false,
  batchLoading: false,
  batchProgress: null,
  error: null,
  redirect: false,
};

export const fetchTicketData = createAsyncThunk(
  "ticket/fetchTicketData",
  async (data, { rejectWithValue }) => {
    try {
      const { page, pageSize, sort, outlet, date_from, date_to, ticket_id } = data;
      const response = await TicketingService.get({
        page,
        pageSize,
        sort,
        outlet,
        date_from,
        date_to,
        ticket_id,
      });
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

export const fetchAllTicketData = createAsyncThunk(
  "ticket/fetchAllTicketData",
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const { sort, outlet, date_from, date_to, ticket_id, batchSize = 5000 } = data;
      const allData = await TicketingService.getAll({
        sort,
        outlet,
        date_from,
        date_to,
        ticket_id,
        batchSize,
        onBatchLoaded: (progress) => {
          dispatch(updateTicketBatchProgress(progress));
        },
      });
      return { data: allData };
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

export const fetchTicketTemplate = createAsyncThunk(
  "ticket/fetchTicketTemplate",
  async (id, { rejectWithValue }) => {
    try {
      const response = await TicketingService.getTemplate(id);
      return response;
    } catch (err) {
      return rejectWithValue(err || "Error");
    }
  }
);

export const fetchTicketDetail = createAsyncThunk(
  "ticket/fetchTicketDetail",
  async (id, { rejectWithValue }) => {
    try {
      const response = await TicketingService.getDetail(id);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

export const createTicketReportingData = createAsyncThunk(
  "ticket/createTicketReportingData",
  async (data, { rejectWithValue }) => {
    try {
      const { outlet, description } = data;
      const response = await TicketingService.createReporting({
        outlet,
        description,
      });
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

export const createTicketItemTransferData = createAsyncThunk(
  "ticket/createTicketItemTransferData",
  async (data, { rejectWithValue }) => {
    try {
      const { outlet, items, fee } = data;
      const response = await TicketingService.createItemTransfer({
        outlet,
        items,
        fee,
      });
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

export const createTicketItemDeliveryData = createAsyncThunk(
  "ticket/createTicketItemDeliveryData",
  async (data, { rejectWithValue }) => {
    try {
      const { input_type, outlet, products, template } = data;
      const response = await TicketingService.createItemDelivery({
        input_type,
        outlet,
        products,
        template,
      });
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

export const updateTicket = createAsyncThunk(
  "ticket/updateTicket",
  async (data, { rejectWithValue }) => {
    try {
      const { url_id, details } = data;
      const response = await TicketingService.updateTicket({
        url_id,
        details,
      });
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

export const approveTicket = createAsyncThunk(
  "ticket/approveTicket",
  async (data, { rejectWithValue }) => {
    try {
      const { url_id, details } = data;
      const response = await TicketingService.approveTicket({
        url_id,
        details,
      });
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

export const rejectTicket = createAsyncThunk(
  "ticket/rejectTicket",
  async (id, { rejectWithValue }) => {
    try {
      const response = await TicketingService.rejectTicket(id);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

export const ticketSlice = createSlice({
  name: "ticket",
  initialState,
  reducers: {
    showLoading: (state) => {
      state.loading = true;
    },
    updateTicketBatchProgress: (state, action) => {
      state.batchProgress = action.payload;
      state.data = { data: action.payload.data };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTicketData.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchTicketData.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
      })
      .addCase(fetchTicketData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchAllTicketData.pending, (state) => {
        state.batchLoading = true;
        state.batchProgress = null;
        state.error = null;
      })
      .addCase(fetchAllTicketData.fulfilled, (state, action) => {
        state.batchLoading = false;
        state.batchProgress = null;
        state.data = action.payload;
      })
      .addCase(fetchAllTicketData.rejected, (state, action) => {
        state.batchLoading = false;
        state.batchProgress = null;
        state.error = action.payload;
      })
      .addCase(fetchTicketDetail.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchTicketDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.dataDetail = action.payload.data;
      })
      .addCase(fetchTicketDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchTicketTemplate.pending, (state) => {
        state.redirect = false;
        state.loading = true;
      })
      .addCase(fetchTicketTemplate.fulfilled, (state, action) => {
        state.loading = false;
        state.dataTemplate = action.payload;
      })
      .addCase(fetchTicketTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createTicketReportingData.pending, (state) => {
        state.loading = true;
        state.redirect = false;
      })
      .addCase(createTicketReportingData.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.redirect = true;
        notification.success({ message: "Proses Berhasil" });
      })
      .addCase(createTicketReportingData.rejected, (state, action) => {
        state.redirect = false;
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createTicketItemTransferData.pending, (state) => {
        state.loading = true;
        state.redirect = false;
      })
      .addCase(createTicketItemTransferData.fulfilled, (state) => {
        state.loading = false;
        // state.data = action.payload.data;
        state.redirect = true;
        notification.success({ message: "Proses Berhasil" });
      })
      .addCase(createTicketItemTransferData.rejected, (state, action) => {
        state.redirect = false;
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createTicketItemDeliveryData.pending, (state) => {
        state.loading = true;
        state.redirect = false;
      })
      .addCase(createTicketItemDeliveryData.fulfilled, (state) => {
        state.loading = false;
        // state.data = action.payload.data;
        state.redirect = true;
        notification.success({ message: "Proses Berhasil" });
      })
      .addCase(createTicketItemDeliveryData.rejected, (state, action) => {
        state.redirect = false;
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateTicket.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateTicket.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = true;
        notification.success({ message: "Proses Berhasil" });
      })
      .addCase(updateTicket.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(approveTicket.pending, (state) => {
        state.loading = true;
      })
      .addCase(approveTicket.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = true;
        notification.success({ message: "Proses Berhasil" });
      })
      .addCase(approveTicket.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(rejectTicket.pending, (state) => {
        state.loading = true;
      })
      .addCase(rejectTicket.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = true;
        notification.success({ message: "Proses Berhasil" });
      })
      .addCase(rejectTicket.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { showLoading, updateTicketBatchProgress } = ticketSlice.actions;

export default ticketSlice.reducer;
